import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, finalize, map, Observable, tap } from 'rxjs';
import { BasicQuoteRequest, IProduct } from '../../models/quote/basic-quote.interface';
import { TravelersStore } from '../../../core/stores/travelers.store';
import { TripDatesStore } from '../../../pages/trip-details/when-are-you-travelling/store/trip-dates.store';
import { DestinationsStore } from '../../../pages/trip-details/where-are-you-travelling/store/destinations.store';
import { isTheSameData } from '@pc/shared';
import { MainStore } from '../../../store/main-store/main-store';
import { TravelersAges } from '../../models/traveler.interface';
import { ISalesResponse, RestApiService } from '@pc/services';
import { ProcessTypeStore } from '../../../store/process-type-store/process-type-store';
import { ProductsStore } from '../../../pages/covers-page/store/products.store';
import { QuoteOverviewService } from './quote-overview.service';

@Injectable({
  providedIn: 'root'
})
export class BasicQuoteService {
  public proposal = computed(() => this.setProposal());
  public getArePayloadsEqual = computed(() => this.arePayloadsEqual());

  private destinationsStore = inject(DestinationsStore);
  private travelersStore = inject(TravelersStore);
  private tripDatesStore = inject(TripDatesStore);

  private mainStore = inject(MainStore);

  private arePayloadsEqual = signal(false);
  private setProposal = signal<IProduct | null>(null);
  private restApiService = inject(RestApiService);
  private agentStore = inject(ProcessTypeStore);
  private productStore = inject(ProductsStore);
  private quoteOverviewService = inject(QuoteOverviewService);

  public getBasicQuote(payload?: Partial<BasicQuoteRequest>): Observable<IProduct[]> {
    this.productStore.setIsLoading(true);
    const request = this.getBasicQuoteForCreation(payload);

    return this.getProduct(request).pipe(
      finalize(() => {
        this.productStore.setIsLoading(false);
      }),
      tap((products) => {
        const product = products[0];

        this.mainStore.setPdsFilePath(product.pdsFilePath);
        this.productStore.setProducts(products);
        if (!this.mainStore.useGateway()) this.productStore.setSelectedProductId(product.policyGroupID);
        this.addProductsToTravelers(products);
        this.quoteOverviewService.setBasePrice(product.basicPrice);
        this.productStore.setIsLoading(false);
      }),
      catchError((error) => {
        this.productStore.setIsLoading(false);
        throw error;
      })
    );
  }

  public getProduct(payload: BasicQuoteRequest): Observable<IProduct[]> {
    this.arePayloadsEqual.set(isTheSameData(payload, this.mainStore.basicQuotePayload()));

    this.mainStore.updateBasicQuotePayload({ ...payload });

    return this.restApiService.post<ISalesResponse<IProduct[]>>(`Quote/Proposal`, payload, true).pipe(
      map((response) => {
        return response.data;
      })
    );
  }

  public getBasicQuoteForCreation(payload: Partial<BasicQuoteRequest> = {}): BasicQuoteRequest {
    const travelersAges: TravelersAges[] = this.travelersStore
      .sortedAllTravelers()
      .filter((t) => t.age >= 0)
      .map((t) => ({
        age: t.age,
        dateOfBirth: this.calculateDateOfBirth(t.age || 0).toISOString(),
        type: t.type,
        participentNum: t.participantNum,
        customerID: t.customerID
      }));

    const defaultPayload: BasicQuoteRequest = {
      travelersAges: travelersAges,
      destinations: this.destinationsStore.pickedDestinations(),
      policyDestinationType: this.mainStore.policyDestinationType(),
      type: 1,
      fromDate: this.tripDatesStore.dates()?.start?.toDateString() || '',
      toDate: this.tripDatesStore.dates()?.end?.toDateString() || '',
      distributorUserId: '',
      processType: this.mainStore.processType(),
      agentSaleNumberId: this.agentStore.agentSaleNumberDetails()?.id || '',
      siteUserId: this.agentStore.siteUserId() || '',
      amtTripDurationId: 0,
      selectedExcessLevelId: this.productStore.selectedExcessId(),
      selectedInsuredAmountId: this.productStore.selectedInsuredAmountId(),
      cfarApproval: false,
      selectedProductId: this.productStore.selectedProductId(),
      stateId: this.travelersStore.stateId(),
      associationId: this.agentStore.organizationId() || ''
    };

    const mergedPayload = { ...defaultPayload, ...payload };

    return mergedPayload as BasicQuoteRequest;
  }

  private calculateDateOfBirth(age: number, referenceDate: Date = new Date()): Date {
    const dateOfBirth = new Date(referenceDate);
    dateOfBirth.setFullYear(dateOfBirth.getFullYear() - age);
    return dateOfBirth;
  }

  private addProductsToTravelers(products: IProduct[]): void {
    if (!this.arePayloadsEqual()) products.forEach((product) => this.travelersStore.addProduct(product.policyGroupID));
  }
}
